import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { NavItems, Orgs } from 'website/navutilities';

const welcomeImages = [
  ...NavItems.map(item => item.backgroundImage),
  ...Orgs.map(item => item.backgroundImage),
].filter(img => img && img.trim().length > 0)
  .slice(1, -1);

const ROTATION_INTERVAL = 5000; // 5 seconds for full rotation
const FADE_DURATION = 500; // 0.5 seconds for fade transition

// Separate component for rotating images
export const RotatingWelcomeImage = React.memo(() => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageOpacity, setImageOpacity] = useState(1);

  useEffect(() => {
    if (welcomeImages.length <= 1) return;

    const rotateImage = () => {
      setImageOpacity(0);
      setTimeout(() => {
        setCurrentImageIndex((prevIndex) => (prevIndex + 1) % welcomeImages.length);
        setImageOpacity(1);
      }, FADE_DURATION);
    };

    const intervalId = setInterval(rotateImage, ROTATION_INTERVAL);

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  return (
    <RotatingImage 
      src={welcomeImages[currentImageIndex]} 
      $opacity={imageOpacity}
    />
  );
});

const RotatingImage = styled.div.attrs({
  className: 'RotatingImage'
})`
  border-radius: 5px;
  width: 250px;
  height: 215px;
  opacity: ${props => props.$opacity};
  transition: opacity 0.5s ease-in-out;
  background-image: url(${props => props.src});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
`;

export const DashboardEmptyState = ({ 
  showArchived = false,
  archivedContent,
  welcomeImage,
  title,
  subtitle,
  actionButton,
}) => {
  return (
    <EmptyStateWrapper>
      {showArchived ? (
        <div style={{
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'flex-start', 
          justifyContent: 'flex-start', 
          position: 'relative'
        }}>
          <div style={{
            position: 'absolute', 
            top: '22px', 
            left: '20px', 
            alignItems: 'flex-start'
          }}>
            <b>{archivedContent}</b>
          </div>
          <img src="https://media1.giphy.com/media/v1.Y2lkPTc5MGI3NjExemJkdjYwMXhybG9sZzV2enl0bWlkYWltdDR0YTg5Z2wyZ2xzdWVteCZlcD12MV9pbnRlcm5hbF9naWZfYnlfaWQmY3Q9Zw/5xaOcLGm3mKRQuDYCgU/giphy.gif" />
        </div> 
      ) : (
        <div>
          {welcomeImage}
          <ContentWrapper>
            {title}
            {subtitle && <i>{subtitle}</i>}
            {actionButton}
          </ContentWrapper>
        </div>
      )}
    </EmptyStateWrapper>
  );
};

const EmptyStateWrapper = styled.div.attrs({
  className: 'EmptyStateWrapper'
})`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 30px 20px;
  color: #666;
  font-size: 14px;
  width: 100%;
  text-align: left;
  background: #1a1a1a;
  border-radius: 5px;
  margin: 5px 0px;
  box-sizing: border-box;
  font-weight: 400;
  color: #c1c1c1;
  font-size: 16px;
  font-weight: 900;
  font-family: 'PT Sans', sans-serif;
  flex-direction: row;
  flex-wrap: wrap;

  div {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    flex-direction: row;
    height: 100%;
    gap: 10px;
  }

  img {
    border-radius: 5px;
    display: flex;
    height: 219px;
  }
`;

const ContentWrapper = styled.span.attrs({
  className: 'ContentWrapper'
})`
  display: flex;
  flex-direction: column;
  justify-content: center;
  background: white;
  font-weight: 300;
  border-radius: 5px;
  padding: 30px;
  background: color(srgb 0.17 0.17 0.17);
  gap: 15px;
  flex: 1;

  i {
    text-decoration: underline;
  }
`;

export const AddButton = styled.button.attrs({
  className: 'AddButton'
})`
  align-self: flex-start;
  padding: 7px 12px 7px;
  font-size: 12px;
  font-weight: 700;
  border-radius: 2px;
  background: ${props => props.$color || '#5000d0'};
  color: white;
  border: none;
  cursor: pointer;
  margin-top: 15px;
  font-family: 'PT Sans', sans-serif;
  animation: ${props => props.$shouldPulse ? 'pulse 2s ease-out infinite' : 'none'};

  @keyframes pulse {
    0% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.2);
      font-weight: 900;
    }
    70% {
      transform: scale(1.05);
      box-shadow: 0 0 0 10px rgba(255, 255, 255, 0);
      font-weight: 700;
    }
    100% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(255, 255, 255, 0);
      font-weight: 900;
    }
  }

  &:hover {
    background: ${props => props.$hoverColor || '#4400b3'};
    transition: background 0.3s ease;
    animation: none;
  }
`; 